import { Button, Dropdown, Tag } from "antd";
import { ReloadOutlined, PlusOutlined, EllipsisOutlined, CloudUploadOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { memo, useEffect, useState } from "react";
import { useAtom, useSetAtom } from 'jotai';
import { s2mosaicItemsLEFTAtom, s2mosaicItemsRIGHTAtom, s2mosaicLoadingAtom } from "./store";
import styled from "styled-components";
import { S2Mosaic_GetPage_ResponseItem, S2MosaicAPIs } from "~/common/typescript/apis/S2MosaicAPIs";
import { toast } from 'react-toastify';
import { API_URL_ADMIN } from "~/config/config";

export interface S2MosaicItemProps {
  item: S2Mosaic_GetPage_ResponseItem;
  onRefresh: (id: string, newWmsUrl: string) => void;
}

const getBoundsFromItem = (item: any): [[number, number], [number, number]] | null => {
  if (!item) return null;
  const bbox = item.bbox;
  if (!bbox) return null;

  if (bbox.coordinates && Array.isArray(bbox.coordinates[0])) {
    const coords = bbox.coordinates[0];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    coords.forEach((pt: any) => {
      const x = Number(pt[0]);
      const y = Number(pt[1]);
      if (!isNaN(x) && !isNaN(y)) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    });
    if (minX !== Infinity && maxX !== -Infinity) {
      return [[minX, minY], [maxX, maxY]];
    }
  }

  if (Array.isArray(bbox) && bbox.length === 4) {
    return [[bbox[0], bbox[1]], [bbox[2], bbox[3]]];
  }

  return null;
};

const S2MosaicItem = (props: S2MosaicItemProps) => {
  const halfWidth = document.body.clientWidth / 2;
  const [listLeft, setListLeft] = useAtom(s2mosaicItemsLEFTAtom);
  const [listRight, setListRight] = useAtom(s2mosaicItemsRIGHTAtom);
  const setS2MosaicLoading = useSetAtom(s2mosaicLoadingAtom);

  const imageDescription = `${dayjs(props.item.imageDescription['start_date']).format('DD/MM/YYYY')} - ${dayjs(props.item.imageDescription['end_date']).format('DD/MM/YYYY')}`;

  const onClickLeft = async () => {
    window.compare.setSlider(halfWidth);
    // Check if the item already exists in the left map
    const exists = listLeft.some(item => item.id === props.item.id);
    if (exists) {
      toast.warning('Ảnh đã tồn tại trong bản đồ bên trái', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
      return;
    }

    setS2MosaicLoading({
      isLoading: true,
      text: `Đang làm mới và chuẩn bị ảnh S2 Mosaic (${imageDescription}) cho bản đồ trái...`,
    });

    try {
      // 1. Luôn gọi refresh WMS để lấy token mới nhất từ GEE và cập nhật DB
      let activeWms = props.item.wmsUrl;
      try {
        const refreshData = await S2MosaicAPIs.remote_RefreshWMS(Number(props.item.id));
        if (refreshData?.wmsUrl) {
          activeWms = refreshData.wmsUrl;
          props.onRefresh(props.item.id, refreshData.wmsUrl);
          setIsExpired(false);
        }
      } catch (e) {
        console.warn('Failed to pre-refresh S2 Mosaic token, falling back to proxy tile URL', e);
      }

      // 2. Sẵn sàng mới thêm lớp vào bản đồ
      setListLeft(prev => [...prev, { ...props.item, wmsUrl: activeWms }]);

      // 3. Phóng tới ảnh trên bản đồ
      const boundsLeft = getBoundsFromItem(props.item);
      const targetMapLeft = window.beforeMap || window.map;
      if (boundsLeft && targetMapLeft) {
        targetMapLeft.fitBounds(boundsLeft, { padding: 40 });
      }

      toast.success('Đã thêm ảnh vào bản đồ bên trái', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
    } finally {
      setS2MosaicLoading({ isLoading: false, text: '' });
    }
  };

  const onClickRight = async () => {
    // Check if the item already exists in the right map
    const exists = listRight.some(item => item.id === props.item.id);
    if (exists) {
      toast.warning('Ảnh đã tồn tại trong bản đồ bên phải', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
      return;
    }

    setS2MosaicLoading({
      isLoading: true,
      text: `Đang làm mới và chuẩn bị ảnh S2 Mosaic (${imageDescription}) cho bản đồ phải...`,
    });

    try {
      // 1. Luôn gọi refresh WMS để lấy token mới nhất từ GEE và cập nhật DB
      let activeWms = props.item.wmsUrl;
      try {
        const refreshData = await S2MosaicAPIs.remote_RefreshWMS(Number(props.item.id));
        if (refreshData?.wmsUrl) {
          activeWms = refreshData.wmsUrl;
          props.onRefresh(props.item.id, refreshData.wmsUrl);
          setIsExpired(false);
        }
      } catch (e) {
        console.warn('Failed to pre-refresh S2 Mosaic token, falling back to proxy tile URL', e);
      }

      // 2. Sẵn sàng mới thêm lớp vào bản đồ
      setListRight(prev => [...prev, { ...props.item, wmsUrl: activeWms }]);

      // 3. Phóng tới ảnh trên bản đồ
      const boundsRight = getBoundsFromItem(props.item);
      const targetMapRight = window.map || window.beforeMap;
      if (boundsRight && targetMapRight) {
        targetMapRight.fitBounds(boundsRight, { padding: 40 });
      }

      toast.success('Đã thêm ảnh vào bản đồ bên phải', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored',
      });
    } finally {
      setS2MosaicLoading({ isLoading: false, text: '' });
    }
  };

  const onClick_ViewOnQGIS = async () => {
    try {
      let activeWms = props.item.wmsUrl;
      if (activeWms && activeWms.includes('earthengine.googleapis.com')) {
        activeWms = `${API_URL_ADMIN}/api/remote-image-alert-ext/sentinel-2-mosaic/tile/{z}/{x}/{y}?image_id=${encodeURIComponent(props.item.id)}`;
      } else if (activeWms && activeWms.startsWith('/')) {
        activeWms = `${API_URL_ADMIN}${activeWms}`;
      }
      const bounds = getBoundsFromItem(props.item);
      const bboxStr = bounds ? `${bounds[0][0]},${bounds[0][1]},${bounds[1][0]},${bounds[1][1]}` : undefined;
      const { sendToQGIS } = await import('~/utils/qgisClient');
      await sendToQGIS({
        url: activeWms,
        name: `S2 Mosaic (${imageDescription})`,
        command: 'ADD_REMOTE_IMAGE',
        bbox: bboxStr,
      });
      toast.success('Đã thêm lớp bản đồ S2 Mosaic lên QGIS', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    } catch (error) {
      toast.error('Lỗi: Hãy thử bật QGIS và bật plugin TLGeo', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      console.log(error);
    }
  };

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  const checkExpiration = async () => {
    let url = props.item.wmsUrl;
    if (!url) {
      setIsExpired(true);
      return;
    }
    if (url.includes('earthengine.googleapis.com')) {
      url = `${API_URL_ADMIN}/api/remote-image-alert-ext/sentinel-2-mosaic/tile/{z}/{x}/{y}?image_id=${encodeURIComponent(props.item.id)}`;
    } else if (url.startsWith('/')) {
      url = `${API_URL_ADMIN}${url}`;
    }
    url = url.replace('{z}', '0').replace('{x}', '0').replace('{y}', '0');
    try {
      const jwt = localStorage.getItem('jwt');
      await axios.get(url, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      });
      setIsExpired(false);
    } catch {
      setIsExpired(true);
    }
  };

  const onClick_Refresh = async () => {
    setIsRefreshing(true);
    try {
      const data = await S2MosaicAPIs.remote_RefreshWMS(Number(props.item.id));
      props.onRefresh(props.item.id, data.wmsUrl);
      setIsExpired(false);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    checkExpiration();
  }, [props.item]);

  const getThumbnailSrc = () => {
    let url = props.item.thumbUrl;
    if (!url) return '';
    if (url.startsWith('/')) {
      return `${API_URL_ADMIN}${url}`;
    }
    return url.replace('http://localhost:11000', API_URL_ADMIN).replace('http://localhost:12000', API_URL_ADMIN);
  };

  // Dropdown menu items
  const menuItems = [
    {
      key: 'add-right',
      label: 'Thêm vào bản đồ phải',
      icon: <PlusOutlined />,
      onClick: onClickRight,
    },
    {
      key: 'add-left',
      label: 'Thêm vào bản đồ trái (để so sánh)',
      icon: <PlusOutlined />,
      onClick: onClickLeft,
    },
    {
      key: 'qgis',
      label: 'Thêm vào QGIS',
      icon: <CloudUploadOutlined />,
      onClick: onClick_ViewOnQGIS,
    },
  ];

  return (
    <UI_Wrapper>
      <UI_Header>
        <UI_TitleContainer>
          <UI_Name title={imageDescription}>{imageDescription}</UI_Name>
          <UI_Sub>{props.item.imageDescription?.name || 'Sentinel-2 GEE Mosaic'}</UI_Sub>
        </UI_TitleContainer>
        <Dropdown 
          menu={{ items: menuItems }} 
          trigger={['click']}
        >
          <Button 
            type="text" 
            icon={<EllipsisOutlined />} 
            size="small"
            onClick={(e) => e.preventDefault()}
          />
        </Dropdown>
      </UI_Header>

      <UI_ImageContainer onClick={onClickRight} title="Nhấp để thêm vào bản đồ phải">
        <img src={getThumbnailSrc()} alt="" loading="lazy" />
        <UI_TagOverlay>
          <Tag color="#1677ff" style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>GEE</Tag>
          <Tag color="#52c41a" style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>S2 Mosaic</Tag>
          <Tag color="#722ed1" style={{ margin: 0, fontSize: 10, lineHeight: '18px' }}>10m</Tag>
        </UI_TagOverlay>
        {isExpired && (
          <UI_ExpiredOverlay>
            <UI_ExpiredText>Ảnh vệ tinh đã hết hạn</UI_ExpiredText>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                onClick_Refresh();
              }} 
              loading={isRefreshing}
            >
              Làm mới
            </Button>
          </UI_ExpiredOverlay>
        )}
      </UI_ImageContainer>

      <UI_Footer>
        <Button size="small" type="primary" ghost onClick={onClickLeft}>
          + Trái
        </Button>
        <Button size="small" type="primary" onClick={onClickRight}>
          + Phải
        </Button>
      </UI_Footer>
    </UI_Wrapper>
  );
};

export default memo(S2MosaicItem);

const UI_Wrapper = styled.div`
  width: 250px;
  height: 270px;
  border-radius: 8px;
  border: 1px solid #eaeaea;
  padding: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #1677ff;
    box-shadow: 0 4px 12px rgba(22, 119, 255, 0.12);
  }
`;

const UI_Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
`;

const UI_TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  margin-right: 6px;
`;

const UI_Name = styled.div`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1f1f1f;
`;

const UI_Sub = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 2px;
`;

const UI_ImageContainer = styled.div`
  position: relative;
  flex: 1;
  margin: 4px 0;
  border-radius: 6px;
  overflow: hidden;
  background: #f0f2f5;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.03);
  }
`;

const UI_TagOverlay = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  display: flex;
  gap: 4px;
`;

const UI_Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

const UI_ExpiredOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  border-radius: 6px;
  z-index: 2;
`;

const UI_ExpiredText = styled.div`
  color: white;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;